import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ScannerCamera = ({ 
  isScanning, 
  onScanResult, 
  onToggleScanning,
  scannerCode,
  isAuthenticated 
}) => {
  const [hasPermission, setHasPermission] = useState(null);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    if (isScanning && hasPermission) {
      startCamera();
    } else {
      stopCamera();
    }

    return () => stopCamera();
  }, [isScanning, hasPermission]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices?.getUserMedia({
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      streamRef.current = stream;
      if (videoRef?.current) {
        videoRef.current.srcObject = stream;
      }
      setHasPermission(true);
      setError(null);
      
      // Simulate QR detection
      setTimeout(() => {
        if (isScanning) {
          simulateQRDetection();
        }
      }, 3000);
    } catch (err) {
      setError('Camera access denied. Please enable camera permissions.');
      setHasPermission(false);
    }
  };

  const stopCamera = () => {
    if (streamRef?.current) {
      streamRef?.current?.getTracks()?.forEach(track => track?.stop());
      streamRef.current = null;
    }
    if (videoRef?.current) {
      videoRef.current.srcObject = null;
    }
  };

  const simulateQRDetection = () => {
    if (!isScanning) return;
    
    setIsProcessing(true);
    
    // Simulate processing delay
    setTimeout(() => {
      const mockScanTypes = ['entry', 'upgrade', 'merchandise', 'member'];
      const randomType = mockScanTypes?.[Math.floor(Math.random() * mockScanTypes?.length)];
      const isValid = Math.random() > 0.2; // 80% success rate
      
      const mockResult = {
        id: 'QR-' + Math.random()?.toString(36)?.substr(2, 9)?.toUpperCase(),
        type: randomType,
        isValid,
        timestamp: new Date(),
        data: {
          ticketId: 'TIX-' + Math.random()?.toString(36)?.substr(2, 6)?.toUpperCase(),
          eventName: 'Summer Music Festival 2024',
          attendeeName: 'John Doe',
          seatSection: randomType === 'entry' ? 'General Admission' : null,
          upgradeType: randomType === 'upgrade' ? 'VIP Access' : null,
          itemName: randomType === 'merchandise' ? 'Festival T-Shirt' : null,
          memberPoints: randomType === 'member' ? Math.floor(Math.random() * 100) + 50 : null
        }
      };
      
      onScanResult(mockResult);
      setIsProcessing(false);
      
      // Trigger haptic feedback on mobile
      if (navigator.vibrate) {
        navigator.vibrate(isValid ? [100] : [100, 50, 100]);
      }
    }, 1500);
  };

  const requestCameraPermission = async () => {
    try {
      await navigator.mediaDevices?.getUserMedia({ video: true });
      setHasPermission(true);
      setError(null);
    } catch (err) {
      setHasPermission(false);
      setError('Camera permission is required for scanning');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="flex-1 flex items-center justify-center bg-muted rounded-lg">
        <div className="text-center p-8">
          <div className="w-16 h-16 bg-error rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Lock" size={32} color="white" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">Authentication Required</h3>
          <p className="text-muted-foreground">Please enter scanner code to access camera</p>
        </div>
      </div>
    );
  }

  if (hasPermission === false) {
    return (
      <div className="flex-1 flex items-center justify-center bg-muted rounded-lg">
        <div className="text-center p-8">
          <div className="w-16 h-16 bg-warning rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Camera" size={32} color="white" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">Camera Access Required</h3>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button variant="default" onClick={requestCameraPermission}>
            Enable Camera
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 relative bg-black rounded-lg overflow-hidden">
      {/* Camera Video */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className="w-full h-full object-cover"
      />
      
      {/* Scanning Overlay */}
      {isScanning && (
        <div className="absolute inset-0 flex items-center justify-center">
          {/* Scanning Frame */}
          <div className="relative">
            <div className="w-64 h-64 border-2 border-accent rounded-lg relative">
              {/* Corner indicators */}
              <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-white rounded-tl-lg"></div>
              <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-white rounded-tr-lg"></div>
              <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-white rounded-bl-lg"></div>
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-white rounded-br-lg"></div>
              
              {/* Scanning line animation */}
              <div className="absolute inset-0 overflow-hidden rounded-lg">
                <div className="w-full h-1 bg-accent animate-pulse absolute top-1/2 transform -translate-y-1/2"></div>
              </div>
            </div>
            
            {/* Instructions */}
            <div className="absolute -bottom-16 left-1/2 transform -translate-x-1/2 text-center">
              <p className="text-white text-sm font-medium bg-black bg-opacity-50 px-4 py-2 rounded-lg">
                {isProcessing ? 'Processing...' : 'Position QR code within frame'}
              </p>
            </div>
          </div>
          
          {/* Processing indicator */}
          {isProcessing && (
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2">
              <div className="bg-black bg-opacity-75 text-white px-4 py-2 rounded-lg flex items-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm">Scanning...</span>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Camera controls */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
        <Button
          variant={isScanning ? "destructive" : "default"}
          size="lg"
          onClick={onToggleScanning}
          iconName={isScanning ? "Square" : "Camera"}
          className="shadow-lg"
        >
          {isScanning ? 'Stop Scanning' : 'Start Scanning'}
        </Button>
      </div>
      
      {/* Scanner info */}
      <div className="absolute top-4 right-4 bg-black bg-opacity-75 text-white px-3 py-2 rounded-lg">
        <div className="flex items-center space-x-2">
          <div className={`w-2 h-2 rounded-full ${isScanning ? 'bg-success animate-pulse' : 'bg-muted-foreground'}`}></div>
          <span className="text-xs font-medium">
            Scanner: {scannerCode || 'Unknown'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ScannerCamera;